'use client';

import * as React from 'react';
import { FLEX_COLUMN_STYLE, FLEX_ROW_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { colors } from '../../css-refs.colors.ts';
import { fontFamilyCSS } from '../../css-refs.typography.ts';
import { spacing } from '../../css-refs.spacing.ts';
import { layoutTokens } from '../../css-refs.layout-tokens.ts';
import { getLabelStyles, getHelperTextStyles } from '../../inputs/form-utils.ts';
import type { ComponentSize } from '@federa/types/ui-design-tokens';
import type { FormFieldState, FormFieldStyles, FormFieldStylesConfig } from '@federa/types/ui-form-field';

const formFieldLabelStyles: React.CSSProperties = getLabelStyles({
	fontFamily: fontFamilyCSS.sans,
	colorOverride: colors.text.primary,
});

/**
 * THE FACE a field's label wears, without the label's BEHAVIOUR (the pointer cursor and the unselectable text are a
 * `<label>`'s, since pressing one focuses its control). Exported for a GROUP'S word standing among fields -- a fieldset
 * legend, a step's title -- so it sits on the fields' own tier by READING it: three Noor forms each typed the same
 * face from a text preset, which held only until this label moved (critics, 2026-09-18).
 */
export const FORM_FIELD_LABEL_FACE: React.CSSProperties = {
	fontFamily: formFieldLabelStyles.fontFamily,
	fontSize: formFieldLabelStyles.fontSize,
	fontWeight: formFieldLabelStyles.fontWeight,
	color: formFieldLabelStyles.color,
};

/** The step between a field's label and its box; a group's word keeps the same step over its rows. */
export const FORM_FIELD_LABEL_GAP = spacing[1.5];

const formFieldContainerStyles: React.CSSProperties = {
	...FLEX_ROW_CENTER_STYLE,
	position: 'relative',
};

/** Wrapper styles for a form field; `gap` defaults to the label step (`FORM_FIELD_LABEL_GAP`). */
export function buildFormFieldWrapperStyles(fullWidth: boolean, gap: string = FORM_FIELD_LABEL_GAP): React.CSSProperties {
	return {
		...FLEX_COLUMN_STYLE,
		gap,
		width: fullWidth ? '100%' : 'auto',
	};
}

/** The helper line under a field, at the FIELD'S size: `size` is the shared helper's own rung selector (an `sm` field
 *  takes the xs helper, larger fields the sm), and it used to be pinned to `'sm'` here whatever the box measured, so an
 *  md field's helper stood a tier under the page's other asides (critics, 2026-09-18). The tertiary ink is this
 *  wrapper's own decision, and the reason it exists (see the sibling suite). */
export function buildFormFieldHelperTextStyles(state: FormFieldState, size: ComponentSize = 'md'): React.CSSProperties {
	return getHelperTextStyles({
		state,
		size,
		fontFamily: fontFamilyCSS.sans,
		colorDefault: colors.text.tertiary,
	});
}

export function getFormFieldFocusRingStyles(isFocused: boolean): React.CSSProperties {
	return isFocused
		? {
				boxShadow: `0 0 0 ${layoutTokens.focus.ring.default.width} ${colors.overlay.brandFocus}`,
			}
		: {};
}

export function useFormFieldStyles(config: FormFieldStylesConfig): FormFieldStyles {
	const { fullWidth, state, size } = config;

	return React.useMemo(
		() => ({
			wrapperStyles: buildFormFieldWrapperStyles(fullWidth),
			labelStyles: formFieldLabelStyles,
			containerStyles: formFieldContainerStyles,
			helperTextStyles: buildFormFieldHelperTextStyles(state, size),
		}),
		[fullWidth, state, size],
	);
}
